import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import UpdateWatcher from "@/components/UpdateWatcher";
import { PaymentReminders, CallList } from "@/components/PaymentReminders";

const L = (load) => lazy(() => load().then((m) => { sessionStorage.removeItem("chunk_reload"); return m; }).catch((e) => {
  // a new release replaced old chunk files: reload once to fetch the fresh version
  if (!sessionStorage.getItem("chunk_reload")) { sessionStorage.setItem("chunk_reload", "1"); window.location.reload(); }
  throw e;
}));
const ChangePassword = L(() => import("@/pages/ChangePassword"));
const Dashboard = L(() => import("@/pages/Dashboard"));
const SalesDashboard = L(() => import("@/pages/SalesDashboard"));
const EmployeeDashboard = L(() => import("@/pages/EmployeeDashboard"));
const Profile = L(() => import("@/pages/Profile"));
const MySettings = L(() => import("@/pages/MySettings"));
const Leads = L(() => import("@/pages/Leads"));
const LeadDetail = L(() => import("@/pages/LeadDetail"));
const Bookings = L(() => import("@/pages/Bookings"));
const BookingDetail = L(() => import("@/pages/BookingDetail"));
const BookingLinks = L(() => import("@/pages/BookingLinks"));
const PublicBooking = L(() => import("@/pages/PublicBooking"));
const Allocation = L(() => import("@/pages/Allocation"));
const Payments = L(() => import("@/pages/Payments"));
const Maids = L(() => import("@/pages/Maids"));
const MaidDetail = L(() => import("@/pages/MaidDetail"));
const ProfileViewer = L(() => import("@/pages/ProfileViewer"));
const Complaints = L(() => import("@/pages/Complaints"));
const ComplaintDetail = L(() => import("@/pages/ComplaintDetail"));
const Replacements = L(() => import("@/pages/Replacements"));
const Employees = L(() => import("@/pages/Employees"));
const ActivityLogs = L(() => import("@/pages/ActivityLogs"));
const Settings = L(() => import("@/pages/Settings"));
const EmailPreview = L(() => import("@/pages/EmailPreview"));
const Customers = L(() => import("@/pages/Customers"));
const CustomerDetail = L(() => import("@/pages/CustomerDetail"));
const Compose = L(() => import("@/pages/Compose"));
const EmailActivity = L(() => import("@/pages/EmailActivity"));
const CommunicationSettings = L(() => import("@/pages/CommunicationSettings"));
const OptionsManager = L(() => import("@/pages/OptionsManager"));
const AiSettings = L(() => import("@/pages/AiSettings"));
const QuickCapture = L(() => import("@/pages/QuickCapture"));
const NewsletterSubscribers = L(() => import("@/pages/NewsletterSubscribers"));
const DiwaliCorner = L(() => import("@/components/diwali/DiwaliCorner"));

const Loading = () => <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">Loading…</div>;
const PageLoading = () => <div className="h-64 rounded-2xl bg-slate-100 animate-pulse" data-testid="page-loading" />;

function AuthOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function Protected({ children, perm }) {
  const { user, can } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (perm && user.role !== "super_admin" && !can(perm)) return <Navigate to="/dashboard" replace />;
  return <Layout><Suspense fallback={<PageLoading />}>{children}</Suspense></Layout>;
}

function DashboardHome() {
  const { user } = useAuth();
  const Board = user?.role === "super_admin" || user?.role === "admin" ? Dashboard : user?.role === "sales" ? SalesDashboard : EmployeeDashboard;
  return <><Suspense fallback={null}><DiwaliCorner /></Suspense><CallList /><PaymentReminders /><Board /></>;
}

function AppRoutes() {
  const P = (el, perm) => <Protected perm={perm}>{el}</Protected>;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/book/:token" element={<PublicBooking />} />
      <Route path="/change-password" element={<AuthOnly><ChangePassword /></AuthOnly>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={P(<DashboardHome />)} />
      <Route path="/leads/quick-capture" element={P(<QuickCapture />, "leads:manage")} />
      <Route path="/leads" element={P(<Leads />, "leads:view")} />
      <Route path="/newsletter" element={P(<NewsletterSubscribers />, "leads:view")} />
      <Route path="/leads/:id" element={P(<LeadDetail />, "leads:view")} />
      <Route path="/bookings" element={P(<Bookings />, "bookings:view")} />
      <Route path="/bookings/:id" element={P(<BookingDetail />, "bookings:view")} />
      <Route path="/booking-links" element={P(<BookingLinks />, "bookings:manage")} />
      <Route path="/allocation" element={P(<Allocation />, "allocation:manage")} />
      <Route path="/maids" element={P(<Maids />, "maids:view")} />
      <Route path="/maids/:id" element={P(<MaidDetail />, "maids:view")} />
      <Route path="/maids/:id/profile" element={<AuthOnly><ProfileViewer /></AuthOnly>} />
      <Route path="/payments" element={P(<Payments />, "payments:view")} />
      <Route path="/complaints" element={P(<Complaints />, "complaints:view")} />
      <Route path="/complaints/:id" element={P(<ComplaintDetail />, "complaints:view")} />
      <Route path="/replacements" element={P(<Replacements />, "replacements:view")} />
      <Route path="/customers" element={P(<Customers />, "customers:view")} />
      <Route path="/customers/:id" element={P(<CustomerDetail />, "customers:view")} />
      <Route path="/communication/compose" element={P(<Compose />, "customers:view")} />
      <Route path="/communication/sent" element={P(<EmailActivity />, "customers:view")} />
      <Route path="/admin/options" element={P(<OptionsManager />, "*")} />
      <Route path="/admin/ai" element={P(<AiSettings />, "*")} />
      <Route path="/communication/settings" element={P(<CommunicationSettings />, "*")} />
      <Route path="/communication/templates" element={P(<EmailPreview />, "*")} />
      <Route path="/employees" element={P(<Employees />, "*")} />
      <Route path="/activity-logs" element={P(<ActivityLogs />)} />
      <Route path="/profile" element={P(<Profile />)} />
      <Route path="/my-settings" element={P(<MySettings />)} />
      <Route path="/settings" element={P(<Settings />, "*")} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<Loading />}><AppRoutes /></Suspense>
        <UpdateWatcher />
      </AuthProvider>
    </BrowserRouter>
  );
}
